import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtEUR, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Package, FileDown, LogOut, Eye, ShoppingBag } from "lucide-react";

const STATUS = {
    pending: { label: "Aguarda pagamento", cls: "bg-paper border-ink" },
    paid: { label: "Pago", cls: "bg-accent-green text-white border-accent-green" },
    failed: { label: "Falhado", cls: "bg-destructive text-white border-destructive" },
    refunded: { label: "Reembolsado", cls: "bg-paper border-ink" },
};

export default function CustomerAccount() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        api.get("/me/orders").then((r) => setOrders(r.data));
    }, []);

    const downloadInvoice = (id, number) => {
        const token = localStorage.getItem("sos_token");
        // Open in new tab with bearer via query? We'll fetch as blob.
        api.get(`/me/orders/${id}/invoice.pdf`, { responseType: "blob" })
            .then((res) => {
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `fatura-${number}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
            });
    };

    return (
        <div className="container-edge py-12" data-testid="customer-account">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
                <div>
                    <div className="label-mono text-magenta">Minha conta</div>
                    <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">
                        Olá, {user?.name?.split(" ")[0] || "cliente"}.
                    </h1>
                    <div className="font-mono text-sm text-ink/60 mt-1">{user?.email}</div>
                </div>
                <button
                    onClick={() => { logout(); nav("/"); }}
                    className="btn-secondary"
                    data-testid="customer-logout"
                >
                    <LogOut size={16} /> Sair
                </button>
            </div>

            <div className="grid md:grid-cols-3 gap-0 border-2 border-ink mb-6">
                {[
                    { icon: ShoppingBag, t: "Encomendas", v: orders.length },
                    { icon: Package, t: "Pagas", v: orders.filter((o) => o.payment_status === "paid").length },
                    { icon: FileDown, t: "Pendentes", v: orders.filter((o) => o.payment_status === "pending").length },
                ].map(({ icon: Icon, t, v }, i) => (
                    <div key={t} className={`p-6 bg-white ${i < 2 ? "md:border-r-2 border-ink" : ""}`}>
                        <Icon className="text-magenta" size={20} />
                        <div className="label-mono mt-3">{t}</div>
                        <div className="font-display font-black text-3xl mt-1">{v}</div>
                    </div>
                ))}
            </div>

            <Link
                to="/conta/wishlist"
                className="inline-flex items-center gap-2 mb-10 font-mono text-sm border-2 border-ink px-4 py-2 hover:bg-magenta hover:text-white transition-colors"
                data-testid="wishlist-link"
            >
                ❤ Ver a minha wishlist
            </Link>

            <h2 className="font-display font-black text-2xl mb-4">Histórico de encomendas</h2>
            <div className="border-2 border-ink bg-white">
                {orders.length === 0 ? (
                    <div className="p-10 text-center font-mono text-ink/60">
                        Ainda não tem encomendas.{" "}
                        <Link to="/loja" className="text-magenta font-bold">
                            Comece a comprar →
                        </Link>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-ink text-white text-xs font-mono uppercase tracking-wider">
                            <tr>
                                <th className="text-left p-3">Nº</th>
                                <th className="text-left p-3 hidden md:table-cell">Data</th>
                                <th className="text-left p-3">Estado</th>
                                <th className="text-right p-3">Total</th>
                                <th className="text-right p-3">Acções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr key={o.id} className="border-t-2 border-ink/10" data-testid={`order-${o.order_number}`}>
                                    <td className="p-3 font-mono">{o.order_number}</td>
                                    <td className="p-3 font-mono text-xs hidden md:table-cell">
                                        {new Date(o.created_at).toLocaleDateString("pt-PT")}
                                    </td>
                                    <td className="p-3">
                                        <span className={`badge-edge ${STATUS[o.payment_status]?.cls || "bg-paper border-ink"}`}>
                                            {STATUS[o.payment_status]?.label || o.payment_status}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right font-mono">{fmtEUR(o.total)}</td>
                                    <td className="p-3 text-right space-x-2 whitespace-nowrap">
                                        <Link to={`/conta/encomendas/${o.id}`} className="text-xs font-bold uppercase hover:text-magenta inline-flex items-center gap-1">
                                            <Eye size={12} /> Ver
                                        </Link>
                                        <button
                                            onClick={() => downloadInvoice(o.id, o.order_number)}
                                            className="text-xs font-bold uppercase hover:text-magenta inline-flex items-center gap-1"
                                            data-testid={`invoice-${o.order_number}`}
                                        >
                                            <FileDown size={12} /> Fatura PDF
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
